import { View, Animated, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useRef, useEffect, useMemo, useState } from 'react';

type EmptyTripsStateProps = {
  onCreateTrip: () => void;
  onAskAI: () => void;
  onConnectEmail?: () => void;
  onBrowseTemplates?: () => void;
  onSmartUpload?: () => void;
  /** Tutorial-only: used for highlighting the "Connect Email" CTA */
  connectEmailTutorialRef?: React.RefObject<View | null>;
  /** Tutorial-only: used for highlighting the "Ask AI" CTA */
  askAiTutorialRef?: React.RefObject<View | null>;
};

export function EmptyTripsState({
  onCreateTrip,
  onAskAI,
  onConnectEmail,
  onBrowseTemplates,
  onSmartUpload,
  connectEmailTutorialRef,
  askAiTutorialRef,
}: EmptyTripsStateProps) {
  const theme = useTheme();
  const [showTip, setShowTip] = useState(false);
  const hasEmailConnect = Boolean(onConnectEmail);
  const hasSmartUpload = Boolean(onSmartUpload);
  const hasTemplates = Boolean(onBrowseTemplates);
  const hasAskAi = Boolean(onAskAI);
  const templatesCtaLabel = Platform.OS === 'web' ? 'Browse templates' : 'Templates';
  const secondaryRowCount = useMemo(() => {
    return [true, hasSmartUpload].filter(Boolean).length;
  }, [hasSmartUpload]);
  
  // Animation for mail icon
  const mailAnim = useRef(new Animated.Value(0)).current;
  const mailScale = useRef(new Animated.Value(1)).current;
  
  // Animation for empty state
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;
  const iconRotate = useRef(new Animated.Value(0)).current;
  const ctaAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
      Animated.loop(
        Animated.sequence([
          Animated.timing(iconRotate, {
            toValue: 1,
            duration: 2000,
            useNativeDriver: true,
          }),
          Animated.timing(iconRotate, {
            toValue: 0,
            duration: 2000,
            useNativeDriver: true,
          }),
        ])
      ),
      Animated.timing(ctaAnim, {
        toValue: 1,
        duration: 400,
        delay: 150,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Mail icon animation (pulsing and floating)
  useEffect(() => {
    if (onConnectEmail) {
      // Floating animation
      Animated.loop(
        Animated.sequence([
          Animated.timing(mailAnim, {
            toValue: 1,
            duration: 2000,
            useNativeDriver: true,
          }),
          Animated.timing(mailAnim, {
            toValue: 0,
            duration: 2000,
            useNativeDriver: true,
          }),
        ])
      ).start();

      // Pulsing animation
      Animated.loop(
        Animated.sequence([
          Animated.timing(mailScale, {
            toValue: 1.1,
            duration: 1500,
            useNativeDriver: true,
          }),
          Animated.timing(mailScale, {
            toValue: 1,
            duration: 1500,
            useNativeDriver: true,
          }),
        ])
      ).start();
    }
  }, [onConnectEmail]);

  const iconRotation = onConnectEmail 
    ? mailAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['-3deg', '3deg'],
      })
    : iconRotate.interpolate({
        inputRange: [0, 1],
        outputRange: ['-3deg', '3deg'],
      });

  const mailTranslateY = mailAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -8],
  });
  
  return (
    <Animated.View
      style={{
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        paddingVertical: theme.spacing(3),
        paddingHorizontal: theme.spacing(3),
        gap: theme.spacing(1.25),
        opacity: fadeAnim,
        transform: [{ scale: scaleAnim }],
        overflow: 'hidden',
        borderRadius: theme.radii.xl,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
      }}
    >
      {hasEmailConnect ? (
        <>
          {/* Compact icon + value proposition (kept small to reduce empty vertical space) */}
          <Animated.View
            style={{
              width: 112,
              height: 112,
              borderRadius: theme.radii.xl,
              backgroundColor: theme.colors.borderMuted,
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: theme.spacing(1),
              alignSelf: 'center',
              transform: [
                { translateY: mailTranslateY },
                { scale: mailScale },
                { rotate: iconRotation },
              ],
              ...theme.shadows.level0,
            }}
          >
            <Ionicons name="mail" size={54} color={theme.colors.primary} />
          </Animated.View>

          <SubHeading style={{ textAlign: 'center', marginTop: theme.spacing(1), fontSize: 22 }}>
            Import your trips in seconds
          </SubHeading>

          <Body
            color={theme.colors.textSecondary}
            style={{
              textAlign: 'center',
              maxWidth: 320,
              lineHeight: 22,
              alignSelf: 'center',
            }}
          >
            Recommended: Connect email to import bookings automatically.
            {'\n'}Or create a trip manually, from a template, or by uploading bookings.
          </Body>

          {/* Primary CTA */}
          <Animated.View
            style={{
              width: '100%',
              marginTop: theme.spacing(1.25),
              gap: theme.spacing(1.25),
              opacity: ctaAnim,
              transform: [
                {
                  translateY: ctaAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [8, 0],
                  }),
                },
              ],
            }}
          >
            <View ref={connectEmailTutorialRef} collapsable={false}>
              <Button
                label="Connect Email"
                onPress={onConnectEmail}
                variant="primary"
                icon={
                  <Ionicons
                    name="mail"
                    size={20}
                    color={theme.colors.surface}
                  />
                }
                labelNumberOfLines={1}
                style={{
                  minHeight: 56,
                  paddingVertical: theme.spacing(1.6),
                  width: '100%',
                }}
              />
            </View>

            <Button
              label="Create manually"
              variant="secondary"
              onPress={onCreateTrip}
              icon={<Ionicons name="add-circle-outline" size={18} color={theme.colors.primary} />}
              labelNumberOfLines={1}
              style={{ minHeight: 54, width: '100%' }}
            />

            {hasTemplates && (
              <Button
                label="Create from template"
                onPress={onBrowseTemplates}
                variant="secondary"
                icon={<Ionicons name="map-outline" size={18} color={theme.colors.primary} />}
                labelNumberOfLines={1}
                style={{ minHeight: 54, width: '100%' }}
              />
            )}

            {/* Smart upload + AI row */}
            {(hasSmartUpload || hasAskAi) && (
              <View style={{ flexDirection: 'row', gap: theme.spacing(1.25), width: '100%', alignSelf: 'stretch' }}>
                {hasSmartUpload ? (
                  <Button
                    label="Upload bookings"
                    variant="secondary"
                    onPress={onSmartUpload}
                    icon={<Ionicons name="cloud-upload-outline" size={18} color={theme.colors.primary} />}
                    size="small"
                    labelNumberOfLines={1}
                    style={{ flex: 1, minHeight: 52, minWidth: 0 }}
                  />
                ) : (
                  <View style={{ flex: 1 }} />
                )}
                {hasAskAi ? (
                  <View ref={askAiTutorialRef} collapsable={false} style={{ flex: 1 }}>
                    <Button
                      label="Ask AI"
                      onPress={onAskAI}
                      variant="secondary"
                      icon={<Ionicons name="sparkles" size={18} color={theme.colors.primary} />}
                      size="small"
                      labelNumberOfLines={1}
                      style={{ flex: 1, minHeight: 52, minWidth: 0 }}
                    />
                  </View>
                ) : (
                  <View style={{ flex: 1 }} />
                )}
              </View>
            )}
          </Animated.View>

          {/* Trust note (collapsed by default) */}
          <View style={{ width: '100%', maxWidth: 360, marginTop: theme.spacing(0.5), alignSelf: 'center' }}>
            {!showTip && (
              <Body
                color={theme.colors.textSecondary}
                style={{ textAlign: 'center', fontSize: 12, lineHeight: 18 }}
              >
                We scan for flight, hotel, and activity confirmations. You choose what to import.
              </Body>
            )}
            <View style={{ alignItems: 'center', marginTop: theme.spacing(0.75) }}>
              <Pressable
                onPress={() => setShowTip((prev) => !prev)}
                accessibilityRole="button"
                accessibilityLabel={showTip ? 'Hide tip' : 'Learn more about email import'}
                style={({ pressed }) => ({
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.pill,
                  backgroundColor: pressed ? theme.colors.borderMuted : 'transparent',
                })}
              >
                <Body style={{ color: theme.colors.primary, fontWeight: '600', fontSize: 13 }}>
                  {showTip ? 'Hide details' : 'Learn more'}
                </Body>
              </Pressable>
            </View>

            {showTip && (
              <View
                style={{
                  backgroundColor: theme.colors.borderMuted,
                  borderRadius: theme.radii.lg,
                  padding: theme.spacing(2),
                  marginTop: theme.spacing(1),
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1.25) }}>
                  <Ionicons name="bulb-outline" size={18} color={theme.colors.primary} />
                  <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                    <Body style={{ fontSize: 13, fontWeight: '700', color: theme.colors.primary }}>
                      Quick tip
                    </Body>
                    <Body style={{ fontSize: 12, lineHeight: 18, color: theme.colors.textSecondary }}>
                      We’ll scan your inbox for booking confirmations and suggest trips. Your emails stay private and secure.
                    </Body>
                  </View>
                </View>
              </View>
            )}
          </View>
        </>
      ) : (
        <>
          {/* Original design when email sync not available */}
          <Animated.View
            style={{
              width: 140,
              height: 140,
              borderRadius: theme.radii.xl,
              backgroundColor: theme.colors.borderMuted,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: theme.spacing(1),
              transform: [{ rotate: iconRotation }],
              ...theme.shadows.level0,
            }}
          >
            <Ionicons name="airplane-outline" size={72} color={theme.colors.primary} />
          </Animated.View>
          
          <SubHeading style={{ textAlign: 'center', marginTop: theme.spacing(1) }}>
            Ready for your next adventure?
          </SubHeading>
          
          <Body
            color={theme.colors.textSecondary}
            style={{
              textAlign: 'center',
              maxWidth: 280,
              lineHeight: 22,
            }}
          >
            Create your first trip to start planning. You can do it manually, from a template, or by uploading bookings.
          </Body>

          {/* Helpful Tips */}
          <View
            style={{
              backgroundColor: theme.colors.borderMuted,
              borderRadius: theme.radii.lg,
              padding: theme.spacing(2),
              marginTop: theme.spacing(2),
              marginBottom: theme.spacing(1),
              width: '100%',
              maxWidth: 320,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1.5) }}>
              <Ionicons name="information-circle-outline" size={20} color={theme.colors.primary} />
              <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                <Body style={{ fontSize: 13, fontWeight: '600', color: theme.colors.primary }}>
                  Getting Started
                </Body>
                <Body style={{ fontSize: 12, lineHeight: 18, color: theme.colors.textSecondary }}>
                  AI can help you plan your trip in seconds, or you can build it step by step yourself.
                </Body>
              </View>
            </View>
          </View>
          
          <Animated.View
            style={{
              gap: theme.spacing(1.5),
              width: '100%',
              marginTop: theme.spacing(3),
              opacity: ctaAnim,
              transform: [
                {
                  translateY: ctaAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [8, 0],
                  }),
                },
              ],
            }}
          >
            <Button
              label="Create manually"
              variant="primary"
              onPress={onCreateTrip}
              icon={<Ionicons name="add-circle-outline" size={20} color={theme.colors.surface} />}
            />
            {onBrowseTemplates && (
              <Button
                label="Create from template"
                variant="secondary"
                onPress={onBrowseTemplates}
                icon={<Ionicons name="map-outline" size={18} color={theme.colors.primary} />}
              />
            )}
            <View style={{ flexDirection: 'row', gap: theme.spacing(1.25), width: '100%' }}>
              {hasSmartUpload ? (
                <Button
                  label="Upload bookings"
                  variant="secondary"
                  onPress={onSmartUpload}
                  icon={<Ionicons name="cloud-upload-outline" size={18} color={theme.colors.primary} />}
                  size="small"
                  style={{ flex: 1, minHeight: 52 }}
                />
              ) : (
                <View style={{ flex: 1 }} />
              )}
              <Button
                label="Ask AI"
                onPress={onAskAI}
                variant="secondary"
              icon={<Ionicons name="sparkles" size={20} color={theme.colors.surface} />}
                size="small"
                style={{ flex: 1, minHeight: 52 }}
              />
            </View>
          </Animated.View>
        </>
      )}
    </Animated.View>
  );
}

